import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  UserPlus, 
  X
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import { testApiConnection, simpleConnectionTest } from '../utils/apiConfig';
import './UserManagement.css';

// Paths relative to axios.defaults.baseURL (/api) — do NOT prefix REACT_APP_API_URL
// or requests become /api/api/... and fail.

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // User form state (used for both create and edit)
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'user'
  });

  useEffect(() => {
    // Test API connection first
    const checkConnection = async () => {
      try {
        const isConnected = await testApiConnection();
        if (isConnected) {
          fetchUsers();
        } else {
          // Try simple connection test as fallback
          const simpleTest = await simpleConnectionTest();
          if (simpleTest) {
            console.log('Server is reachable, trying to fetch users...');
            fetchUsers();
          } else {
            toast.error('Cannot connect to backend server. Please check if the server is running on the correct port.');
          }
        }
      } catch (error) {
        console.error('Connection test error:', error);
        toast.error('Error testing server connection. Please check if the backend is running.');
      }
    };
    
    checkConnection();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/user-management/users');
      
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.code === 'ERR_NETWORK') {
        toast.error('Cannot connect to server. Please check if the backend is running.');
      } else {
        toast.error('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({ ...prev, [field]: value }));
  };

  const resetUserForm = () => {
    setUserForm({
      username: '',
      password: '',
      email: '',
      firstName: '',
      lastName: '',
      role: 'user'
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userForm.username || !userForm.password || !userForm.email) {
      toast.error('Username, password, and email are required');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post('/user-management/register', userForm);
      
      if (response.data.success) {
        toast.success('User created successfully');
        resetUserForm();
        setShowCreateModal(false);
        fetchUsers();
      } else {
        toast.error(response.data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username || '',
      password: '', // Don't pre-fill password
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role || 'user'
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const updateData = { ...userForm };
      // Only include password if it's been changed
      if (!updateData.password) {
        delete updateData.password;
      }
      
      const response = await axios.put(`/user-management/users/${editingUser._id}`, updateData);
      
      if (response.data.success) {
        toast.success('User updated successfully');
        setEditingUser(null);
        resetUserForm();
        fetchUsers();
      } else {
        toast.error(response.data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`/user-management/users/${userId}`);
      
      if (response.data.success) {
        toast.success('User deleted successfully');
        fetchUsers(); // Refresh user list
      } else {
        toast.error(response.data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="user-management-container">
      <Header
        title="User Management"
        subtitle="Manage users and permissions"
      />

      <div className="users-section-header">
        <h2>All Users</h2>
        <button 
          className="create-user-btn"
          onClick={() => {
            resetUserForm();
            setShowCreateModal(true);
          }}
        >
          <UserPlus size={18} />
          Create New User
        </button>
      </div>

      <div className="content-section">
        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Created</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-users">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user._id}>
                      <td>{user.username}</td>
                      <td>{user.email || '-'}</td>
                      <td>
                        {user.firstName || user.lastName 
                          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                          : '-'
                        }
                      </td>
                        <td>
                          <span className={`role-badge ${user.role}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>{formatDate(user.createdAt)}</td>
                      <td>{user.lastLogin ? formatDate(user.lastLogin) : 'Never'}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="action-btn edit"
                            title="Edit User"
                          >
                            ✏️
                          </button>
                          {user._id !== currentUser?._id && (
                            <button
                              onClick={() => handleDeleteUser(user._id)}
                              className="action-btn delete"
                              title="Delete User"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New User</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetUserForm();
                }}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="form-container">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => handleUserFormChange('username', e.target.value)}
                  placeholder="Enter username"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => handleUserFormChange('password', e.target.value)}
                  placeholder="Enter password"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => handleUserFormChange('email', e.target.value)}
                  placeholder="Enter email"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={userForm.firstName}
                    onChange={(e) => handleUserFormChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={userForm.lastName}
                    onChange={(e) => handleUserFormChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={userForm.role}
                  onChange={(e) => handleUserFormChange('role', e.target.value)}
                  className="form-input"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetUserForm();
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => {
          setEditingUser(null);
          resetUserForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit User: {editingUser.username}</h3>
              <button
                onClick={() => {
                  setEditingUser(null);
                  resetUserForm();
                }}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="form-container">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={userForm.username}
                  disabled
                  className="form-input disabled"
                />
              </div>

              <div className="form-group">
                <label>Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => handleUserFormChange('password', e.target.value)}
                  placeholder="Enter new password"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => handleUserFormChange('email', e.target.value)}
                  placeholder="Enter email"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={userForm.firstName}
                    onChange={(e) => handleUserFormChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={userForm.lastName}
                    onChange={(e) => handleUserFormChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={userForm.role}
                  onChange={(e) => handleUserFormChange('role', e.target.value)}
                  className="form-input"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setEditingUser(null);
                    resetUserForm();
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
